import React, {useEffect} from 'react'
import {Box, Link, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import CloudinaryImage from '../../../cloudinary-media/image'
import PropTypes from 'prop-types'
import Title from './partials/title'
import StoryButton from './partials/button'
import StoryDescription from './partials/description'
import Video from './partials/video'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'
import ShopThisLookModal from '../shop-the-look/partials/modal'
import {useDisclosure} from '@chakra-ui/react'
import {useGlobaleContext} from '../../../../contexts/globale'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../../constants'
import {useProducts} from '@salesforce/commerce-sdk-react'
import LoadingSpinner from '../../../loading-spinner'
import {useIntl} from 'react-intl'

export const PlpStoryComponent = (props) => {
    const {
        eagerLoadImage = false,
        componentSize,
        desktopImage,
        mobileImage,
        imageAlt,
        buttonText,
        buttonLink,
        buttonColor,
        title,
        titleColor,
        titleTag,
        titleSizeDesktop,
        titleSizeMobile,
        description,
        descriptionFontFamily,
        descriptionColor,
        desktopVideo,
        desktopVideoThumbnail,
        mobileVideo,
        mobileVideoThumbnail,
        videoAlt,
        titleFontFamily,
        typeId,
        regions,
        enableStl = false,
        id,
        ...rest
    } = props
    const {countryISO, currencyCode} = useGlobaleContext()
    const intl = useIntl()
    const modalDisclosure = useDisclosure()
    const allComponents =
        regions?.components?.[0].regions?.flatMap((region) => region.components ?? []) ?? []
    const currentIndex = allComponents.findIndex((c) => c.id === id)

    const shouldDisplayVideo = desktopVideo && mobileVideo
    const pdTagManager = usePdTagManager()
    const componentName = title || 'PLP Story Component'

    const productIds = []
    let index = 1
    while (`product${index}` in rest) {
        productIds.push(rest[`product${index}`])
        delete rest[`product${index}`] // Avoid modifying rest if unnecessary
        index++
    }
    const enabledScapi =
        Boolean(productIds.length) && !isServer && modalDisclosure.isOpen && enableStl
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const {data: stlProducts, isFetching: isProductsFetching} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: enabledScapi,
            keepPreviousData: true
        }
    )

    useEffect(() => {
        if (!allComponents.length) return
        pdTagManager.viewComponent(typeId, title, allComponents.length)
    }, [])
    const analyticsProps = {
        component_id: typeId,
        component_name: title
    }

    const isWhiteBg = (colorValue) => {
        const color = colorValue?.trim().toLowerCase()
        return color === 'white' || color === '#fff' || color === '#ffffff'
    }
    const getHoverMix = (bg) =>
        isWhiteBg(bg)
            ? 'color-mix(in srgb, white 90%, black 10%)'
            : `color-mix(in srgb, ${bg} 88%, white 12%)`
    const hexToRgba = (hex, alpha = 1) => {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
        hex = hex?.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
                  result[3],
                  16
              )}, ${alpha})`
            : hex
    }
    const ctaButtonText =
        rest?.imageCta ||
        intl.formatMessage({
            id: 'shop.this.look',
            defaultMessage: 'Shop This Look'
        })
    return (
        <Box
            width="100%"
            overflow="hidden"
            className="plp-story-component"
            data-pageid={id}
            cursor="pointer"
        >
            {isProductsFetching && <LoadingSpinner position="fixed" top="auto" />}
            <Box
                gridArea={componentSize === 'small' ? '300/375' : '608/375.96'}
                position="relative"
            >
                <Link
                    href={buttonLink}
                    cursor={buttonLink ? 'pointer' : 'unset'}
                    onClick={() => {
                        pdTagManager.selectComponent({
                            componentId: typeId || '',
                            componentName: componentName || '',
                            titleName: componentName,
                            titlePosition: currentIndex,
                            linkUrl: buttonLink
                        })
                        if (enableStl) {
                            modalDisclosure.onOpen()
                        }
                    }}
                >
                    {shouldDisplayVideo ? (
                        <Video
                            desktopVideo={desktopVideo}
                            mobileVideo={mobileVideo || desktopVideo}
                            desktopVideoThumbnail={desktopVideoThumbnail}
                            mobileVideoThumbnail={mobileVideoThumbnail}
                            videoAlt={videoAlt}
                            aspectRatio={componentSize === 'small' ? '300/375' : '608/375.96'}
                            cloudinaryAspectRatio={componentSize === 'small' ? '4:5' : '16:10'}
                        />
                    ) : (
                        <>
                            <CloudinaryImage
                                src={desktopImage}
                                aspectRatio={componentSize === 'small' ? '4:5' : '16:10'}
                                styles={{
                                    display: {base: 'none', md: 'block'},
                                    aspectRatio:
                                        componentSize === 'small' ? '300/375' : '608/375.96'
                                }}
                                imgSizes={{
                                    md: componentSize === 'small' ? '25vw' : '50vw',
                                    base: componentSize === 'small' ? '50vw' : '100vw'
                                }}
                                sizes={
                                    componentSize === 'small'
                                        ? [225, 325, 384, 576, 650, 768, 960]
                                        : [450, 650, 768, 1152, 1290, 1536, 1920]
                                }
                                alt={imageAlt}
                                lazyload={!eagerLoadImage}
                            />
                            <CloudinaryImage
                                src={mobileImage}
                                aspectRatio={componentSize === 'small' ? '4:5' : '16:10'}
                                styles={{
                                    display: {base: 'block', md: 'none'},
                                    aspectRatio:
                                        componentSize === 'small' ? '300/375' : '608/375.96'
                                }}
                                imgSizes={{
                                    md: componentSize === 'small' ? '25vw' : '50vw',
                                    base: componentSize === 'small' ? '50vw' : '100vw'
                                }}
                                sizes={
                                    componentSize === 'small'
                                        ? [225, 325, 384, 576, 650, 768, 960]
                                        : [450, 650, 768, 1152, 1290, 1536, 1920]
                                }
                                alt={imageAlt}
                                lazyload={!eagerLoadImage}
                            />
                        </>
                    )}
                </Link>
                {!rest?.hideCta && enableStl && (
                    <Button
                        variant="button"
                        onClick={() => {
                            modalDisclosure.onOpen()
                        }}
                        pointerEvents="auto"
                        className="shop-this-look-btn"
                        background={hexToRgba(rest?.btnBgColor, 0.5)}
                        backgroundColor={hexToRgba(rest?.btnBgColor, 0.5)}
                        _hover={{
                            background: hexToRgba(rest?.btnBgColor, 1),
                            opacity: 1,
                            textDecoration: 'none'
                        }}
                        fontWeight="400"
                        padding="4px 25px !important"
                        color={rest?.btnTextColor}
                        fontFamily={rest?.buttonFontFamily}
                        borderRadius="40px"
                        aria-label={ctaButtonText}
                        position="absolute"
                        maxWidth={{base: 'calc(100% - 20px)', md: 'unset'}}
                        minHeight="auto"
                        size={rest?.ctaSize}
                        bottom="20px"
                        left="50%"
                        transform="translateX(-50%)"
                        sx={{
                            _hover: {
                                backgroundColor: getHoverMix(rest?.btnBgColor)
                            }
                        }}
                    >
                        {ctaButtonText}
                    </Button>
                )}
            </Box>
            <Box
                textAlign="left"
                margin={{
                    base: componentSize === 'small' ? '0 20px' : '0 30px',
                    md: componentSize === 'small' ? '0 20px' : '0 30px',
                    lg: componentSize === 'small' ? '0 20px' : '0 30px',
                    xl: '0 30px'
                }}
            >
                {title && (
                    <Title
                        title={title}
                        color={titleColor}
                        tag={titleTag}
                        titleSizeDesktop={titleSizeDesktop}
                        titleSizeMobile={titleSizeMobile}
                        titleFontFamily={titleFontFamily}
                    />
                )}
                {description && (
                    <StoryDescription
                        description={description}
                        descriptionFontFamily={descriptionFontFamily}
                        descriptionColor={descriptionColor}
                    />
                )}
                {buttonText && (
                    <StoryButton
                        buttonLink={buttonLink}
                        buttonText={buttonText}
                        buttonColor={buttonColor}
                    />
                )}
            </Box>
            {enableStl && (
                <ShopThisLookModal
                    desktopImage={desktopImage}
                    mobileImage={mobileImage}
                    desktopVideo={desktopVideo}
                    mobileVideo={mobileVideo}
                    desktopVideoThumbnail={desktopVideoThumbnail}
                    mobileVideoThumbnail={mobileVideoThumbnail}
                    videoAlt={videoAlt}
                    videoEnableSound={false}
                    videoDisableAutoPlay={false}
                    cloudinaryAspectRatio={undefined}
                    products={stlProducts}
                    modalDisclosure={modalDisclosure}
                    analyticsProps={analyticsProps}
                />
            )}
        </Box>
    )
}

PlpStoryComponent.displayName = 'PlpStoryComponent'

PlpStoryComponent.propTypes = {
    eagerLoadImage: PropTypes.bool,
    id: PropTypes.string,
    componentSize: PropTypes.string,
    desktopImage: PropTypes.string,
    mobileImage: PropTypes.string,
    imageAlt: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string,
    buttonColor: PropTypes.string,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    titleTag: PropTypes.string,
    titleSizeDesktop: PropTypes.string,
    titleSizeMobile: PropTypes.string,
    titleSizeTablet: PropTypes.string,
    description: PropTypes.string,
    descriptionColor: PropTypes.string,
    descriptionFontFamily: PropTypes.string,
    desktopVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideo: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    titleFontFamily: PropTypes.string,
    videoAlt: PropTypes.string,
    typeId: PropTypes.string,
    regions: PropTypes.object,
    enableStl: PropTypes.bool
}

export default PlpStoryComponent
